/**
 * Collapsible titled section — one accordion row with a header (title +
 * optional subtitle + optional right-aligned summary) and an expandable body.
 *
 * Extracted from ``PoolSettingsDrawer.PoolSettingsCollapsible`` so the room
 * settings drawer AND the "Create a Room" (NEW_POOL) release dialog render
 * byte-identical section headers — the create-room form is structured to
 * mirror the evolved room drawer (sections pre-filled + collapsed, expand to
 * edit). Purely presentational; the caller owns open/onToggle state.
 */
import React from 'react';

/**
 * @param {{
 *   id: string,
 *   title: string,
 *   subtitle?: string,
 *   summary?: React.ReactNode,  // right-aligned pre-filled value shown while collapsed
 *   open: boolean,
 *   onToggle: () => void,
 *   children: React.ReactNode,
 * }} props
 */
export default function CollapsibleSection({ id, title, subtitle, summary, open, onToggle, children }) {
  const btnId = `${id}-toggle`;
  return (
    <section className="border-b border-gray-200 bg-white">
      <button
        type="button"
        id={btnId}
        className="flex w-full items-start justify-between gap-2 px-4 py-3 text-left hover:bg-gray-50/80"
        aria-expanded={open}
        aria-controls={`${id}-panel`}
        onClick={onToggle}
        data-testid={`section-toggle-${id}`}
      >
        <div className="min-w-0">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-700">{title}</h3>
          {subtitle ? (
            <p className="mt-0.5 text-[11px] text-gray-500">{subtitle}</p>
          ) : null}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {/* Pre-filled summary — only while collapsed, so the user sees the
              current value without expanding (most sections keep defaults). */}
          {!open && summary ? (
            <span className="max-w-[14rem] truncate text-[11px] text-gray-500">{summary}</span>
          ) : null}
          <svg
            className={`mt-0.5 h-4 w-4 shrink-0 text-gray-600 transition-transform ${open ? 'rotate-90' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </div>
      </button>
      {open ? (
        <div
          id={`${id}-panel`}
          role="region"
          aria-labelledby={btnId}
          className="space-y-0 border-t border-gray-100 px-4 pb-3 pt-2"
        >
          {children}
        </div>
      ) : null}
    </section>
  );
}
